<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useStore, useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';
import AgentsAPI from 'dashboard/api/agents';
import AddAgent from './AddAgent.vue';
import AgentPermissionsModal from './AgentPermissionsModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { isAdminOrSuperAdmin } = usePermissions();

const PERSONAL_OWNER_EMAIL = 'victorguzmanfotografia@hotmail.com';

const agentList = computed(() => getters['agents/getAgents'].value || []);
const branchList = computed(() => store.getters['branches/getBranches'] || []);
const currentUser = useMapGetter('getCurrentUser');

const personalBranch = computed(() => branchList.value.find(b => b.is_personal));
const otherBranches = computed(() => branchList.value.filter(b => !b.is_personal));

const searchQuery = ref('');
const showAddPopup = ref(false);

const filteredAgents = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return agentList.value;
  return agentList.value.filter(a =>
    (a.name || '').toLowerCase().includes(q) ||
    (a.email || '').toLowerCase().includes(q)
  );
});

// Agentes que aparecen en la columna de UNA sucursal: solo si su primary_branch_id
// apunta a esa sucursal. Si no tienen primary asignado, NO aparecen en ninguna columna
// de sucursal — caen en la columna "Sin asignar".
function agentsForBranch(branchId, _branchIsPersonal = false) {
  if (!filteredAgents.value.length) return [];
  return filteredAgents.value.filter(a => a.primary_branch_id === branchId);
}

// Agentes sin primary_branch_id seteado (legacy o nuevos sin configurar).
// Se muestran en una columna especial al final del kanban para que el admin
// los configure.
const unassignedAgents = computed(() => {
  return filteredAgents.value.filter(a => !a.primary_branch_id);
});

const isPersonalColumnAllowed = computed(() =>
  currentUser.value?.email === PERSONAL_OWNER_EMAIL
);

const stats = computed(() => {
  const total = agentList.value.length;
  const superAdmins = agentList.value.filter(a => a.role === 'administrator' && (a.accessible_branch_ids || []).length === 0).length;
  const admins = agentList.value.filter(a => a.role === 'administrator' && (a.accessible_branch_ids || []).length > 0).length;
  const agents = agentList.value.filter(a => a.role !== 'administrator').length;
  const branchCount = branchList.value.length;
  return { total, superAdmins, admins, agents, branchCount };
});

const saving = ref({});

async function patchAgentBranches(agentId, payload) {
  saving.value[agentId] = true;
  try {
    await AgentsAPI.update(agentId, payload);
    await store.dispatch('agents/get');
    useAlert('✓ Actualizado');
  } catch (e) {
    useAlert(e.response?.data?.message || 'Error al actualizar');
  } finally {
    saving.value[agentId] = false;
  }
}

// Drag a una columna de sucursal: la convierte en la PRINCIPAL del agente
// (también se asegura de que esté en accessible_branch_ids).
function onAddToColumn(agent, branch) {
  if (!agent || !branch) return;
  if (branch.is_personal) {
    useAlert('⚠ Personal solo permite al dueño · no se puede añadir otros');
    return;
  }
  const accessible = [...(agent.accessible_branch_ids || [])];
  if (!accessible.includes(branch.id)) accessible.push(branch.id);
  patchAgentBranches(agent.id, {
    accessible_branch_ids: accessible,
    primary_branch_id: branch.id,
  });
}

// Quitar el ✕ en una card · quita la sucursal del acceso y, si era la principal,
// limpia el primary_branch_id (el agente cae a la columna Sin asignar).
function onRemoveFromColumn(agent, branchId) {
  if (!agent) return;
  const accessible = (agent.accessible_branch_ids || []).filter(id => id !== branchId);
  const payload = { accessible_branch_ids: accessible };
  if (agent.primary_branch_id === branchId) payload.primary_branch_id = null;
  patchAgentBranches(agent.id, payload);
}

// Drag a la columna "Sin asignar": limpia el primary_branch_id pero conserva el acceso.
function onMoveToUnassigned(agent) {
  if (!agent) return;
  if (!agent.primary_branch_id) return;
  patchAgentBranches(agent.id, { primary_branch_id: null });
}

// ─── Eliminar usuario (mismas reglas que la vista Lista · regla 2026-05-19 Victor) ───
const currentUserId = computed(() => getters.getCurrentUserID.value);
const verifiedAdministrators = computed(() =>
  agentList.value.filter(a => a.role === 'administrator' && a.confirmed)
);

function canDeleteAgent(agent) {
  if (!isAdminOrSuperAdmin.value) return false;
  if (currentUserId.value === agent.id) return false;
  if (agent.super_admin) return false;
  if (!agent.confirmed) return true;
  if (agent.role === 'administrator') return verifiedAdministrators.value.length !== 1;
  return true;
}

const showDeletePopup = ref(false);
const agentToDelete = ref({});
const deleteMessage = computed(() => ` ${agentToDelete.value.name}?`);
const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${agentToDelete.value.name}`
);
const deleteRejectText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${agentToDelete.value.name}`
);
function openDeletePopup(agent) {
  agentToDelete.value = agent;
  showDeletePopup.value = true;
}
function closeDeletePopup() {
  showDeletePopup.value = false;
}
async function confirmDeleteAgent() {
  const id = agentToDelete.value.id;
  closeDeletePopup();
  try {
    await store.dispatch('agents/delete', id);
    await store.dispatch('agents/get');
    useAlert(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (e) {
    useAlert(e.response?.data?.error || t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
}

function getInitials(name) {
  if (!name) return '??';
  return name.split(' ').slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
}

// ─── Drill-down monocromo · 2026-05-22 (Victor) ─────────────────────
// Pills de sucursal en orden fijo (SDO · Santiago · La Romana · SDE) →
// columnas por departamento de la sucursal activa.
// Catálogo de cargos · ÚNICA fuente de verdad: account.custom_attributes
// .agent_departments (editable en el modal del agente). Sin esto, las columnas
// quedaban hardcodeadas y no reflejaban renombrar/añadir/eliminar (Victor 2026-05-23).
const DEFAULT_DEPARTMENTS = ['Administración', 'Gerencia', 'Recepción', 'Video', 'Fotógrafo', 'Maquillistas'];
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentAccount = computed(
  () => store.getters['accounts/getAccount'](accountId.value) || {}
);
const DEPARTMENTS = computed(() => {
  const d = currentAccount.value.custom_attributes?.agent_departments;
  return Array.isArray(d) && d.length ? d : DEFAULT_DEPARTMENTS;
});
const DEPT_COLUMNS = computed(() => [...DEPARTMENTS.value, 'Sin departamento']);
const BRANCH_ORDER = ['sdo', 'stg', 'lrm', 'sde'];
const orderedBranches = computed(() =>
  [...otherBranches.value].sort((a, b) => {
    const ra = BRANCH_ORDER.indexOf((a.code || '').toLowerCase());
    const rb = BRANCH_ORDER.indexOf((b.code || '').toLowerCase());
    const pa = ra === -1 ? 99 : ra;
    const pb = rb === -1 ? 99 : rb;
    if (pa !== pb) return pa - pb;
    return (a.position || 0) - (b.position || 0);
  })
);
const pillBranches = computed(() => {
  const arr = [...orderedBranches.value];
  if (personalBranch.value && isPersonalColumnAllowed.value) arr.push(personalBranch.value);
  return arr;
});
const activeKey = ref(null); // branch id | 'unassigned'
const activeBranchId = computed(() =>
  activeKey.value === null ? (orderedBranches.value[0]?.id ?? null) : activeKey.value
);
const currentBranchObj = computed(() => branchList.value.find(b => b.id === activeBranchId.value) || null);
const activeCount = computed(() =>
  activeKey.value === 'unassigned'
    ? unassignedAgents.value.length
    : filteredAgents.value.filter(a => a.primary_branch_id === activeBranchId.value).length
);
function deptOf(agent) { return agent.custom_attributes?.department || 'Sin departamento'; }
function branchAgentCount(b) { return filteredAgents.value.filter(a => a.primary_branch_id === b.id).length; }
function agentsForActiveDept(dept) {
  if (activeKey.value === 'unassigned') return dept === 'Sin departamento' ? unassignedAgents.value : [];
  return filteredAgents.value.filter(
    a => a.primary_branch_id === activeBranchId.value && deptOf(a) === dept
  );
}
async function setAgentDepartment(agent, dept) {
  if (!agent) return;
  const value = dept === 'Sin departamento' ? '' : dept;
  if (deptOf(agent) === dept) return;
  saving.value[agent.id] = true;
  try {
    await AgentsAPI.update(agent.id, { agent: { department: value } });
    await store.dispatch('agents/get');
  } catch (e) {
    useAlert(e.response?.data?.message || 'Error al actualizar');
  } finally {
    saving.value[agent.id] = false;
  }
}

function getAgentBranchOthers(agent, currentBranchId) {
  return (agent.accessible_branch_ids || [])
    .filter(id => id !== currentBranchId)
    .map(id => branchList.value.find(b => b.id === id))
    .filter(Boolean);
}

function branchClass(branch) {
  if (branch.is_personal) return 'pers';
  const code = (branch.code || '').toLowerCase();
  if (code === 'lrm') return 'lrm';
  if (code === 'sde') return 'sde';
  if (code === 'sdo') return 'sdo';
  if (code === 'stg') return 'stg';
  return 'other';
}

function avatarColor(agent) {
  const colors = [
    'linear-gradient(135deg, #fbbf24, #d97706)',
    'linear-gradient(135deg, #f472b6, #be185d)',
    'linear-gradient(135deg, #818cf8, #6366f1)',
    'linear-gradient(135deg, #34d399, #059669)',
    'linear-gradient(135deg, #60a5fa, #1d4ed8)',
    'linear-gradient(135deg, #c084fc, #7c3aed)',
  ];
  return colors[agent.id % colors.length];
}

// Presencia real del agente (Victor 2026-06-09): availability_status del store
// (online/busy/offline) se actualiza en vivo por el canal de presencia del CRM.
// 🟢 conectado · 🟡 ocupado · ⚪ desconectado. Misma fuente que Eficiencia.
const presence = agent => agent.availability_status || 'offline';
const presenceLabel = s =>
  s === 'online' ? 'Conectado' : s === 'busy' ? 'Ocupado' : 'Desconectado';

function rolePill(agent) {
  if (agent.role === 'administrator') {
    if ((agent.accessible_branch_ids || []).length === 0) return { name: '⭐ SUPER', cls: 'super' };
    return { name: '🛡 ADMIN', cls: 'admin' };
  }
  return { name: '👤 AGENTE', cls: 'agent' };
}

function openAddPopup() { showAddPopup.value = true; }
function closeAddPopup() { showAddPopup.value = false; }

const showPermissionsModal = ref(false);
const currentAgent = ref({});
function openPermissions(agent) {
  currentAgent.value = agent;
  showPermissionsModal.value = true;
}
function closePermissions() { showPermissionsModal.value = false; }
async function onPermissionsSaved() {
  // Esperar a que el store tenga los datos frescos ANTES de cerrar el modal,
  // para que las columnas del kanban re-renderen con primary_branch_id actualizado.
  await store.dispatch('agents/get');
  showPermissionsModal.value = false;
}

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('branches/get');
});
</script>

<template>
  <div class="ak-page">
    <div class="ak-page-head">
      <div class="ak-page-title">
        <div class="ak-page-title-icon">👥</div>
        <div>
          <div class="ak-page-title-h">Agentes por sucursal</div>
          <div class="ak-page-title-sub">Por sucursal y departamento · arrastra para cambiar cargo · clic abre permisos</div>
        </div>
      </div>
      <div class="ak-head-right">
        <div class="ak-toolbar">
          <router-link :to="{ name: 'agent_list', params: { accountId: $route.params.accountId } }" class="ak-toggle-btn">
            📋 Lista
          </router-link>
          <span class="ak-toggle-btn ak-toggle-active">🗂 Kanban</span>
        </div>
        <button class="ak-btn-primary" @click="openAddPopup">+ Añadir agente</button>
      </div>
    </div>

    <div class="ak-stats">
      <strong>{{ stats.total }} agentes</strong>
      en {{ stats.branchCount }} sucursales ·
      {{ stats.superAdmins }} super admins ·
      {{ stats.admins }} admins ·
      {{ stats.agents }} {{ stats.agents === 1 ? 'agente' : 'agentes' }}
      <div class="ak-search">
        <span class="ak-search-icon">🔍</span>
        <input v-model="searchQuery" placeholder="Buscar agente o email…" />
      </div>
    </div>

    <!-- Monocromo drill-down · 2026-05-22 (Victor) -->
    <div class="mono-wrap">
      <div class="mono-pills">
        <button
          v-for="b in pillBranches"
          :key="`pill-${b.id}`"
          class="mono-pill"
          :class="{ on: activeKey !== 'unassigned' && activeBranchId === b.id }"
          @click="activeKey = b.id"
        >{{ b.is_personal ? '🔒 ' : '' }}{{ b.name }}<span class="n">{{ branchAgentCount(b) }}</span></button>
        <button
          v-if="unassignedAgents.length"
          class="mono-pill"
          :class="{ on: activeKey === 'unassigned' }"
          @click="activeKey = 'unassigned'"
        >∅ Sin asignar<span class="n">{{ unassignedAgents.length }}</span></button>
      </div>

      <div class="mono-crumb">
        <span class="mono-bcode">{{ activeKey === 'unassigned' ? '∅' : (currentBranchObj?.code || '—') }}</span>
        <span class="mono-bn">{{ activeKey === 'unassigned' ? 'Sin sucursal principal' : (currentBranchObj?.name || '') }}</span>
        <span class="mono-meta">{{ activeCount }} agentes</span>
      </div>

      <div class="mono-cols">
        <!-- Sin asignar: una sola columna -->
        <div v-if="activeKey === 'unassigned'" class="mono-col" style="flex:1">
          <div class="mono-col-h"><span class="t">Sin sucursal principal</span><span class="c">{{ unassignedAgents.length }}</span></div>
          <div class="mono-col-body">
            <div v-for="agent in unassignedAgents" :key="`u-${agent.id}`" class="mono-row" @click="openPermissions(agent)">
              <div class="mono-av" :class="'pres-' + presence(agent)">{{ getInitials(agent.name) }}<span class="mono-presdot" :title="presenceLabel(presence(agent))"></span></div>
              <div class="mono-info"><div class="mono-name">{{ agent.name }}</div><div class="mono-email">{{ agent.email }}</div></div>
              <div v-if="canDeleteAgent(agent)" class="mono-del" title="Eliminar usuario" @click.stop="openDeletePopup(agent)">🗑</div>
            </div>
          </div>
        </div>

        <!-- Columnas por departamento de la sucursal activa -->
        <template v-else>
          <div v-for="(dept, di) in DEPT_COLUMNS" :key="`dept-${dept}`" class="mono-col">
            <div class="mono-col-h"><span class="t">{{ dept }}</span><span class="c">{{ agentsForActiveDept(dept).length }}</span></div>
            <Draggable
              :model-value="agentsForActiveDept(dept)"
              :group="{ name: 'deptagents', pull: 'clone', put: true }"
              item-key="id"
              class="mono-col-body"
              ghost-class="mono-row-ghost"
              :animation="160"
              @change="(evt) => evt.added && setAgentDepartment(evt.added.element, dept)"
            >
              <template #item="{ element: agent }">
                <div class="mono-row" @click="openPermissions(agent)">
                  <div class="mono-av" :class="'pres-' + presence(agent)">{{ getInitials(agent.name) }}<span class="mono-presdot" :title="presenceLabel(presence(agent))"></span></div>
                  <div class="mono-info"><div class="mono-name">{{ agent.name }}</div><div class="mono-email">{{ agent.email }}</div></div>
                  <div v-if="canDeleteAgent(agent)" class="mono-del" title="Eliminar usuario" @click.stop="openDeletePopup(agent)">🗑</div>
                </div>
              </template>
            </Draggable>
            <div v-if="!agentsForActiveDept(dept).length" class="mono-empty">arrastra aquí</div>
          </div>
        </template>
      </div>
    </div>

    <div class="ak-hint"><strong>💡 Tip:</strong> elige la sucursal arriba · <b>arrastra</b> un agente entre departamentos para cambiar su cargo · <b>clic</b> abre sus permisos · <b>🗑</b> elimina al usuario (solo super admin)</div>

    <woot-modal v-model:show="showAddPopup" :on-close="closeAddPopup">
      <AddAgent @close="closeAddPopup" />
    </woot-modal>

    <AgentPermissionsModal
      v-if="showPermissionsModal && currentAgent.id"
      :agent="currentAgent"
      @close="closePermissions"
      @saved="onPermissionsSaved"
    />

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeleteAgent"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>

<style scoped>
.ak-page { width: calc(100% + 48px); margin: -16px -24px -32px; background: #050505; min-height: calc(100vh - 0px); padding: 24px 28px 80px; display: flex; flex-direction: column; color: #fff; font-family: -apple-system, system-ui, sans-serif; }

.ak-page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; gap: 16px; flex-wrap: wrap; }
.ak-page-title { display: flex; align-items: center; gap: 12px; }
.ak-page-title-icon { width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(217,119,6,0.06)); border: 1px solid rgba(251,191,36,0.45); display: flex; align-items: center; justify-content: center; font-size: 18px; color: #fbbf24; }
.ak-page-title-h { font-size: 22px; font-weight: 900; color: #fff; }
.ak-page-title-sub { font-size: 12px; color: rgba(255,255,255,0.55); margin-top: 2px; }
.ak-head-right { display: flex; align-items: center; gap: 12px; }

.ak-btn-primary { padding: 10px 18px; background: linear-gradient(135deg, #fbbf24, #d97706); color: #18181b; border: none; border-radius: 9px; font-weight: 900; font-size: 12px; cursor: pointer; box-shadow: 0 4px 14px rgba(251,191,36,0.40); }
.ak-btn-primary:hover { filter: brightness(1.05); }

.ak-toolbar { display: flex; gap: 4px; padding: 3px; background: rgba(0,0,0,0.30); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; }
.ak-toggle-btn { padding: 5px 14px; font-size: 11.5px; font-weight: 800; color: rgba(255,255,255,0.55); cursor: pointer; border-radius: 6px; transition: all 0.18s; text-decoration: none; }
.ak-toggle-btn:hover { color: #fff; }
.ak-toggle-active { background: linear-gradient(135deg, #fbbf24, #d97706); color: #18181b; }

.ak-stats { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; font-size: 11px; color: rgba(255,255,255,0.70); }
.ak-stats strong { color: #fff; }
.ak-search { margin-left: auto; padding: 6px 11px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10); border-radius: 8px; display: flex; align-items: center; gap: 6px; min-width: 240px; }
.ak-search-icon { color: rgba(255,255,255,0.45); }
.ak-search input { flex: 1; background: transparent; border: none; outline: none; color: #fff; font-size: 11.5px; font-family: inherit; }

.ak-kanban { flex: 1; display: flex; gap: 12px; overflow-x: auto; padding-bottom: 8px; }
.ak-col { flex: 1 1 280px; min-width: 260px; display: flex; flex-direction: column; background: rgba(15,15,18,0.50); border: 1.5px solid; border-radius: 12px; overflow: hidden; max-height: calc(100vh - 240px); }
.ak-col.pers { border-color: rgba(99,102,241,0.45); }
.ak-col.lrm { border-color: rgba(168,85,247,0.45); }
.ak-col.sde { border-color: rgba(34,197,94,0.45); }
.ak-col.sdo { border-color: rgba(59,130,246,0.45); }
.ak-col.stg { border-color: rgba(245,158,11,0.45); }
.ak-col.other { border-color: rgba(255,255,255,0.20); }
.ak-col-unassigned { border-color: rgba(239,68,68,0.40); border-style: dashed; }
.ak-col-unassigned .ak-col-head { background: linear-gradient(135deg, rgba(239,68,68,0.08), transparent); }
.ak-col-icon-unassigned { background: linear-gradient(135deg, rgba(239,68,68,0.40), rgba(190,18,60,0.20)); color: #fca5a5; font-size: 14px; font-weight: 900; }
.ak-col-name-unassigned { color: #fca5a5; }
.ak-col-count-unassigned { background: rgba(239,68,68,0.25); border-color: rgba(239,68,68,0.50); color: #fca5a5; }

.ak-col-head { padding: 12px 14px; display: flex; align-items: center; justify-content: space-between; }
.ak-col.pers .ak-col-head { background: linear-gradient(135deg, rgba(99,102,241,0.10), transparent); }
.ak-col.lrm .ak-col-head { background: linear-gradient(135deg, rgba(168,85,247,0.10), transparent); }
.ak-col.sde .ak-col-head { background: linear-gradient(135deg, rgba(34,197,94,0.10), transparent); }
.ak-col.sdo .ak-col-head { background: linear-gradient(135deg, rgba(59,130,246,0.10), transparent); }
.ak-col.stg .ak-col-head { background: linear-gradient(135deg, rgba(245,158,11,0.10), transparent); }

.ak-col-title { display: flex; align-items: center; gap: 8px; }
.ak-col-icon { width: 28px; height: 28px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 9.5px; font-weight: 900; color: #fff; flex-shrink: 0; }
.ak-col-icon.pers { background: linear-gradient(135deg, #818cf8, #6366f1); }
.ak-col-icon.lrm { background: linear-gradient(135deg, #c084fc, #7c3aed); }
.ak-col-icon.sde { background: linear-gradient(135deg, #34d399, #059669); }
.ak-col-icon.sdo { background: linear-gradient(135deg, #60a5fa, #1d4ed8); }
.ak-col-icon.stg { background: linear-gradient(135deg, #fbbf24, #c2410c); color: #18181b; }
.ak-col-icon.other { background: linear-gradient(135deg, #94a3b8, #64748b); }

.ak-col-name { font-size: 12px; font-weight: 900; letter-spacing: 0.5px; }
.ak-col.pers .ak-col-name { color: #a5b4fc; }
.ak-col.lrm .ak-col-name { color: #c4b5fd; }
.ak-col.sde .ak-col-name { color: #6ee7b7; }
.ak-col.sdo .ak-col-name { color: #93c5fd; }
.ak-col.stg .ak-col-name { color: #fcd34d; }
.ak-col-meta { font-size: 9.5px; color: rgba(255,255,255,0.50); margin-top: 1px; }

.ak-col-count { padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 900; flex-shrink: 0; }
.ak-col.pers .ak-col-count { background: rgba(99,102,241,0.20); color: #a5b4fc; border: 1px solid rgba(99,102,241,0.45); }
.ak-col.lrm .ak-col-count { background: rgba(168,85,247,0.20); color: #c4b5fd; border: 1px solid rgba(168,85,247,0.45); }
.ak-col.sde .ak-col-count { background: rgba(34,197,94,0.20); color: #6ee7b7; border: 1px solid rgba(34,197,94,0.45); }
.ak-col.sdo .ak-col-count { background: rgba(59,130,246,0.20); color: #93c5fd; border: 1px solid rgba(59,130,246,0.45); }
.ak-col.stg .ak-col-count { background: rgba(245,158,11,0.20); color: #fcd34d; border: 1px solid rgba(245,158,11,0.45); }

.ak-col-body { flex: 1; overflow-y: auto; padding: 8px; display: flex; flex-direction: column; gap: 6px; min-height: 80px; }

.ak-card { padding: 10px 12px; background: rgba(15,15,18,0.92); border: 1px solid rgba(255,255,255,0.10); border-radius: 9px; cursor: grab; transition: all 0.18s; }
.ak-card:hover { border-color: rgba(251,191,36,0.40); transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,0.45); }
.ak-card:active { cursor: grabbing; }
.ak-card-ghost { opacity: 0.4; }

.ak-card-row { display: flex; align-items: center; gap: 10px; }
.ak-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 900; color: #fff; flex-shrink: 0; }
.ak-info { flex: 1; min-width: 0; }
.ak-name { font-size: 12px; font-weight: 800; color: #fff; }
.ak-email { font-size: 9.5px; color: rgba(255,255,255,0.50); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
.ak-card-actions { display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
.ak-remove-btn { width: 22px; height: 22px; border-radius: 5px; background: rgba(239,68,68,0.10); border: 1px solid rgba(239,68,68,0.30); color: #f87171; display: flex; align-items: center; justify-content: center; font-size: 14px; cursor: pointer; opacity: 0; transition: opacity 0.18s; }
.ak-card:hover .ak-remove-btn { opacity: 1; }
.ak-remove-btn:hover { background: rgba(239,68,68,0.25); }
.ak-delete-btn { width: 22px; height: 22px; border-radius: 5px; background: rgba(239,68,68,0.16); border: 1px solid rgba(239,68,68,0.45); display: flex; align-items: center; justify-content: center; font-size: 12px; cursor: pointer; opacity: 0; transition: opacity 0.18s, background 0.18s; }
.ak-card:hover .ak-delete-btn { opacity: 1; }
.ak-delete-btn:hover { background: rgba(239,68,68,0.40); }

.ak-card-foot { display: flex; align-items: center; gap: 6px; margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.06); }
.ak-role-pill { padding: 1px 6px; border-radius: 4px; font-size: 8.5px; font-weight: 900; letter-spacing: 0.3px; }
.ak-role-pill.super { background: rgba(251,191,36,0.20); color: #fbbf24; }
.ak-role-pill.admin { background: rgba(239,68,68,0.18); color: #f87171; }
.ak-role-pill.agent { background: rgba(59,130,246,0.18); color: #60a5fa; }

.ak-others { font-size: 9px; color: rgba(255,255,255,0.45); display: flex; align-items: center; gap: 3px; margin-left: 4px; }
.ak-other-dot { width: 8px; height: 8px; border-radius: 50%; }
.ak-other-dot.pers { background: #818cf8; }
.ak-other-dot.lrm { background: #a855f7; }
.ak-other-dot.sde { background: #22c55e; }
.ak-other-dot.sdo { background: #3b82f6; }
.ak-other-dot.stg { background: #f59e0b; }

.ak-hint { position: fixed; bottom: 16px; left: 50%; transform: translateX(-50%); padding: 8px 16px; background: rgba(0,0,0,0.85); border: 1px solid rgba(251,191,36,0.30); border-radius: 8px; font-size: 11px; color: rgba(255,255,255,0.85); z-index: 10; }
.ak-hint strong { color: #fbbf24; }

/* ─── Monocromo ejecutivo · 2026-05-22 (Victor) · estilo oficial ─── */
.mono-wrap { flex: 1; background: linear-gradient(165deg,#1a1a1e 0%,#121214 45%,#0c0c0e 100%); border: 1px solid rgba(255,255,255,0.07); border-radius: 16px; box-shadow: 0 30px 70px -36px #000, inset 0 1px 0 rgba(255,255,255,0.03); overflow: hidden; display: flex; flex-direction: column; }
.mono-pills { display: flex; gap: 8px; flex-wrap: wrap; padding: 16px 22px 6px; }
.mono-pill { font-size: 11.5px; font-weight: 600; padding: 8px 15px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.07); color: rgba(255,255,255,0.55); background: rgba(255,255,255,0.02); cursor: pointer; font-family: inherit; transition: transform 0.16s, box-shadow 0.16s, border-color 0.16s, color 0.16s; }
.mono-pill:hover { transform: translateY(-2px); border-color: rgba(255,255,255,0.16); color: #f0f0f3; }
.mono-pill.on { background: rgba(201,163,90,0.10); border-color: rgba(201,163,90,0.45); color: #c9a35a; box-shadow: 0 8px 22px -12px rgba(201,163,90,0.5); }
.mono-pill .n { margin-left: 7px; font-size: 9px; opacity: 0.7; font-variant-numeric: tabular-nums; }
.mono-crumb { display: flex; align-items: center; gap: 10px; padding: 14px 22px 4px; }
.mono-bcode { font-size: 8px; font-weight: 800; letter-spacing: 0.05em; padding: 3px 7px; border-radius: 5px; background: rgba(255,255,255,0.05); color: rgba(201,163,90,0.75); border: 1px solid rgba(255,255,255,0.07); }
.mono-bn { font-size: 13px; font-weight: 600; color: #f0f0f3; }
.mono-meta { margin-left: auto; font-size: 10.5px; color: rgba(255,255,255,0.34); }
.mono-cols { display: flex; gap: 0; padding: 8px 10px 22px; overflow-x: auto; flex: 1; perspective: 1000px; }
.mono-col { flex: 1; min-width: 200px; padding: 10px 16px; position: relative; }
.mono-col + .mono-col::before { content: ""; position: absolute; left: 0; top: 14px; bottom: 14px; width: 1px; background: rgba(255,255,255,0.07); }
.mono-col-h { display: flex; align-items: center; gap: 8px; padding-bottom: 10px; margin-bottom: 8px; }
.mono-col-h .t { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 700; color: rgba(201,163,90,0.75); }
.mono-col-h .c { margin-left: auto; font-size: 10px; color: rgba(255,255,255,0.34); font-variant-numeric: tabular-nums; }
.mono-col-body { min-height: 30px; }
.mono-row { display: flex; align-items: center; gap: 9px; padding: 7px 9px; border-radius: 9px; cursor: pointer; transform-style: preserve-3d; transition: transform 0.18s cubic-bezier(0.2,0.7,0.3,1), box-shadow 0.18s, background 0.18s; }
.mono-row:hover { transform: translateY(-3px) scale(1.025); background: rgba(255,255,255,0.05); box-shadow: 0 12px 28px -10px rgba(0,0,0,0.85), 0 0 0 1px rgba(201,163,90,0.18); }
.mono-row-ghost { opacity: 0.4; }
.mono-av { position: relative; width: 25px; height: 25px; border-radius: 50%; background: linear-gradient(135deg,#3a3a42,#222228); border: 1px solid rgba(255,255,255,0.06); display: flex; align-items: center; justify-content: center; font-size: 8.5px; font-weight: 600; color: rgba(255,255,255,0.78); flex-shrink: 0; }
/* Presencia real (Victor 2026-06-09): punto pulsante 🟢 conectado · 🟡 ocupado · ⚪ desconectado */
.mono-presdot { position: absolute; bottom: -1px; right: -1px; width: 9px; height: 9px; border-radius: 50%; border: 2px solid #0b0b0d; background: #44454a; }
.mono-av.pres-online .mono-presdot { background: #34c759; animation: monoPulseG 1.6s infinite; }
.mono-av.pres-busy .mono-presdot { background: #e0a23c; animation: monoPulseA 1.8s infinite; }
@keyframes monoPulseG { 0% { box-shadow: 0 0 0 0 rgba(52,199,89,.55); } 70% { box-shadow: 0 0 0 6px rgba(52,199,89,0); } 100% { box-shadow: 0 0 0 0 rgba(52,199,89,0); } }
@keyframes monoPulseA { 0% { box-shadow: 0 0 0 0 rgba(224,162,60,.5); } 70% { box-shadow: 0 0 0 5px rgba(224,162,60,0); } 100% { box-shadow: 0 0 0 0 rgba(224,162,60,0); } }
.mono-info { flex: 1; min-width: 0; }
.mono-name { font-size: 11.5px; font-weight: 500; color: rgba(255,255,255,0.86); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mono-email { font-size: 9px; color: rgba(255,255,255,0.34); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mono-del { opacity: 0; font-size: 12px; cursor: pointer; flex-shrink: 0; transition: opacity 0.15s; }
.mono-row:hover .mono-del { opacity: 0.6; }
.mono-del:hover { opacity: 1; }
.mono-empty { font-size: 9.5px; color: rgba(255,255,255,0.22); text-align: center; padding: 10px 0; border: 1px dashed rgba(255,255,255,0.06); border-radius: 8px; margin-top: 4px; }
</style>
